import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, toast } from '@mlm/ui';
import { apiErrorPayload, deleteLessonFile, uploadLessonFile, type LessonFile } from '../lib/api';

/**
 * A lesson's downloadable handouts — worksheets, PDFs, decks.
 *
 * Extracted rather than inlined because `LessonRow` is already 400+ lines. It
 * owns no list of its own: `files` comes from the parent's course query and
 * `onChange` refetches it, so an upload and a delete can never leave the panel
 * showing something the server disagrees with.
 *
 * Attachments are PAID content even on a free-preview lesson (the download
 * route requires full course access), and they follow the lesson's release
 * schedule — the copy below says both, because an author choosing what to
 * attach to a preview lesson is exactly who needs to know.
 *
 * The upload is STAGED behind its own button. Picking a file used to BE the
 * upload, so the title cleared itself the instant the request succeeded and
 * authors read that as "it threw my title away" — they had typed the title
 * first, which is the natural order. Now the file is held, the title stays
 * editable (and pre-fills from the file name), and nothing is sent until the
 * author presses Upload.
 */
export function LessonAttachmentsPanel({
  lessonId,
  files,
  onChange,
}: {
  lessonId: string;
  files: LessonFile[];
  onChange: () => void;
}) {
  const [title, setTitle] = useState('');
  const [pending, setPending] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const clearPicker = () => {
    // Re-picking the SAME file after a failure fires no change event unless
    // the input is cleared (the video upload learned this first).
    if (fileRef.current) fileRef.current.value = '';
  };

  const upload = useMutation({
    meta: {
      successMessage: 'Attachment uploaded.',
      silentErrorCodes: ['file_too_large', 'unsupported_file_type', 'file_infected'],
    },
    mutationFn: (file: File) =>
      uploadLessonFile(lessonId, {
        file,
        // An untitled upload is named after the file, minus its extension —
        // better than refusing the click or storing an empty label.
        title: title.trim() || file.name.replace(/\.[^.]+$/, ''),
      }),
    onSuccess: () => {
      setTitle('');
      setPending(null);
      clearPicker();
      onChange();
    },
    onError: (err) => {
      const code = apiErrorPayload(err)?.error;
      toast.error(
        code === 'file_too_large'
          ? 'That file is over 25 MB.'
          : code === 'unsupported_file_type'
            ? 'PDF, Word, Excel, PowerPoint or images only.'
            : code === 'file_infected'
              ? 'The virus scanner rejected this file.'
              : 'Could not upload — try again.',
      );
    },
  });

  const remove = useMutation({
    meta: { successMessage: 'Attachment removed.' },
    mutationFn: (fileId: string) => deleteLessonFile(lessonId, fileId),
    onSuccess: onChange,
  });

  return (
    <div className="space-y-2 rounded border p-2">
      <p className="text-xs font-medium">Downloadable resources</p>
      {files.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          No resources yet. Learners download these from the lesson page.
        </p>
      ) : (
        <ul className="space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate">
                {f.title}
                {f.originalFilename && (
                  <span className="text-muted-foreground"> · {f.originalFilename}</span>
                )}
              </span>
              <span className="flex-none tabular-nums text-muted-foreground">
                {(f.sizeBytes / 1048576).toFixed(1)} MB
              </span>
              {f.scanStatus === 'pending' && (
                <span className="flex-none text-muted-foreground">scan pending</span>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={remove.isPending}
                onClick={() => remove.mutate(f.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Input
        value={title}
        placeholder="Title (defaults to the file name)"
        disabled={upload.isPending}
        onChange={(e) => setTitle(e.target.value)}
      />
      <Input
        ref={fileRef}
        type="file"
        className="max-w-full"
        accept=".pdf,.xlsx,.docx,.pptx,.png,.jpg,.jpeg"
        disabled={upload.isPending}
        onChange={(e) => setPending(e.target.files?.[0] ?? null)}
      />
      {pending && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={upload.isPending} onClick={() => upload.mutate(pending)}>
            {upload.isPending ? 'Uploading…' : `Upload ${pending.name}`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={upload.isPending}
            onClick={() => {
              setPending(null);
              clearPicker();
            }}
          >
            Cancel
          </Button>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        PDF, Word, Excel, PowerPoint or images, up to 25 MB. They follow this lesson&apos;s release
        schedule, and on a free-preview lesson a learner still needs the full course to download
        them.
      </p>
    </div>
  );
}
